import { PUBLISH_MODAL_COPY } from './PublishQuoteModalV2.constants'
import type { PublishQuoteModalSubmitData } from './types'
import type { PublishRecipients } from './usePublishRecipients'

/**
 * What a publish modal variant hands back on submit: the ticked recipients and
 * their note. The header turns it into the publish body.
 */
export const publishSubmitData = ({
  recipients,
  message,
  approvalComment,
  isApprovalRequired
}: {
  recipients: PublishRecipients
  message: string
  approvalComment: string
  isApprovalRequired: boolean
}): PublishQuoteModalSubmitData => {
  const isSharing = recipients.emails.length > 0

  return {
    recipients: recipients.emails,
    // An empty note, or one nobody is being sent, is nothing to pass on.
    emailMessage: isSharing && message.trim() ? message : undefined,
    approvalComment: isApprovalRequired ? approvalComment : undefined
  }
}

export type PublishButtonCopy = {
  submitButton: string
  submitAndShareButton: string
}

export const PUBLISH_BUTTON_COPY = {
  publish: {
    submitButton: PUBLISH_MODAL_COPY.recipients.publishButton,
    submitAndShareButton: PUBLISH_MODAL_COPY.recipients.publishAndShareButton
  },
  publishChanges: {
    submitButton: PUBLISH_MODAL_COPY.recipients.publishChangesButton,
    submitAndShareButton:
      PUBLISH_MODAL_COPY.recipients.publishChangesAndShareButton
  },
  republish: {
    submitButton: PUBLISH_MODAL_COPY.recipients.republishButton,
    submitAndShareButton: PUBLISH_MODAL_COPY.recipients.republishAndShareButton
  }
} as const satisfies Record<string, PublishButtonCopy>

/**
 * What the submit button says. Approval wins over everything: that run ends in
 * a review, not an email. Otherwise the label picks up "and share" once
 * somebody is ticked.
 */
export const publishSubmitButtonText = ({
  isApprovalRequired,
  recipients,
  copy
}: {
  isApprovalRequired: boolean
  recipients: PublishRecipients
  copy: PublishButtonCopy
}): string => {
  if (isApprovalRequired) {
    return PUBLISH_MODAL_COPY.approvalRequired.submitButton
  }

  // Read off the emails rather than the ticks, so the label can't promise to
  // share with somebody the list no longer holds.
  return recipients.emails.length > 0
    ? copy.submitAndShareButton
    : copy.submitButton
}
